import { InverseBoldMatch } from "@faire/prototype-playground/components/shared/InverseBoldMatch";
import { cn } from "@faire/prototype-playground/lib/utils";
import { Close } from "@faire/slate-icons/Close";
import { ImageSearch } from "@faire/slate-icons/ImageSearch";
import { RewindClock } from "@faire/slate-icons/RewindClock";
import { Search } from "@faire/slate-icons/Search";
import React, { useEffect, useRef } from "react";

import type { MockReorderedProduct } from "./mockProducts";
import { ReorderedProductRow } from "./ReorderedProductRow";
import type { RelatedSearch } from "./searchModel";

interface MockBrandRow {
  id: string;
  name: string;
  initial: string;
  avatarBackgroundClass: string;
}

const MOCK_BRANDS: MockBrandRow[] = [
  {
    id: "b_catherine_tough",
    name: "Catherine Tough",
    initial: "C",
    avatarBackgroundClass: "bg-[#ffe4e6]",
  },
  {
    id: "b_verloop_knits",
    name: "VERLOOP | knits",
    initial: "V",
    avatarBackgroundClass: "bg-[#dff2fe]",
  },
  {
    id: "b_arctic_fox",
    name: "Arctic Fox & Co.",
    initial: "A",
    avatarBackgroundClass: "bg-[#f1f5f9]",
  },
  {
    id: "b_griffon_1924",
    name: "GRIFFON 1924",
    initial: "G",
    avatarBackgroundClass: "bg-[#fef3c6]",
  },
];

interface TypeaheadSearchBarProps {
  query: string;
  onQueryChange: (next: string) => void;
  /** Commits a search — Enter in the input, or picking a suggestion or recent. */
  onSubmitQuery: (query: string) => void;
  isOpen: boolean;
  onOpenChange: (next: boolean) => void;
  reorderedMatches: MockReorderedProduct[];
  showReorderedSection: boolean;
  suggestionRows: { text: string }[];
  /** Shown with a thumbnail under "Related to your search" when the query is empty. */
  suggestedSearches: RelatedSearch[];
  /** Shown under "Recent searches", below the suggested searches. */
  recentQueries: string[];
  now: Date;
}

export const TypeaheadSearchBar: React.FC<TypeaheadSearchBarProps> = ({
  query,
  onQueryChange,
  onSubmitQuery,
  isOpen,
  onOpenChange,
  reorderedMatches,
  showReorderedSection,
  suggestionRows,
  suggestedSearches,
  recentQueries,
  now,
}) => {
  const hasQuery = query.length > 0;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <div ref={wrapperRef} className="relative z-10 w-full">
      <div
        role="button"
        tabIndex={0}
        aria-label="Open search"
        onClick={() => onOpenChange(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpenChange(true);
          }
        }}
        className="border-fs-border-muted bg-fs-surface-primary flex h-10 cursor-text items-center gap-(--slate-dimensions-3) rounded-full border px-(--slate-dimensions-4) transition-shadow duration-150 ease-out hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)]"
      >
        <Search width="20" height="20" />
        {hasQuery ? (
          <span className="type-fs-paragraph text-fs-text-primary flex-1 truncate">
            {query}
          </span>
        ) : (
          <span className="type-fs-paragraph text-fs-text-subdued flex-1">
            Search for products or brands
          </span>
        )}
        {hasQuery ? (
          <>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onQueryChange("");
              }}
              aria-label="Clear search"
              className="text-fs-text-primary"
            >
              <Close width="16" height="16" />
            </button>
            <div className="bg-fs-border-muted h-4 w-px" />
          </>
        ) : null}
        <ImageSearch width="20" height="20" />
      </div>

      <div
        aria-hidden={!isOpen}
        className={cn(
          "bg-fs-surface-primary ring-fs-border-muted absolute inset-x-0 z-30 origin-top overflow-hidden rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.08)] ring-1 transition-[opacity,top] duration-150 ease-out",
          isOpen
            ? "pointer-events-auto -top-1 opacity-100"
            : "pointer-events-none top-0 opacity-0"
        )}
      >
        <div
          className={cn(
            "transition-[padding] duration-150 ease-out",
            isOpen ? "py-1" : "py-0"
          )}
        >
          <div className="flex h-10 items-center gap-(--slate-dimensions-3) px-(--slate-dimensions-4)">
            <Search width="20" height="20" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  onSubmitQuery(query);
                } else if (event.key === "Escape") {
                  onOpenChange(false);
                }
              }}
              placeholder="Search for products or brands"
              className="type-fs-paragraph text-fs-text-primary placeholder:text-fs-text-subdued flex-1 border-0 bg-transparent outline-none"
              aria-label="Search"
            />
            {query.length > 0 ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onQueryChange("");
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
                className="text-fs-text-primary"
              >
                <Close width="16" height="16" />
              </button>
            ) : null}
          </div>
        </div>

        <div
          className={cn(
            "transition-[max-height] duration-150 ease-out",
            isOpen ? "max-h-[820px]" : "max-h-0"
          )}
        >
          <div>
            {hasQuery ? (
              <>
                {showReorderedSection ? (
                  <div className="py-(--slate-dimensions-1)">
                    {reorderedMatches.map((product) => (
                      <ReorderedProductRow
                        key={product.id}
                        product={product}
                        query={query}
                        now={now}
                      />
                    ))}
                  </div>
                ) : null}

                <div className="py-(--slate-dimensions-1)">
                  {suggestionRows.map((row) => (
                    <SuggestionRow
                      key={row.text}
                      text={row.text}
                      query={query}
                      onSelect={() => onSubmitQuery(row.text)}
                    />
                  ))}
                </div>

                <div className="px-(--slate-dimensions-5) py-(--slate-dimensions-1)">
                  <div className="type-fs-paragraph-medium text-fs-text-primary mb-(--slate-dimensions-2)">
                    Brands
                  </div>
                  {MOCK_BRANDS.map((brand) => (
                    <BrandRow key={brand.id} brand={brand} />
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="py-(--slate-dimensions-1)">
                  <SectionLabel>Related to your search</SectionLabel>
                  {suggestedSearches.map((search) => (
                    <SuggestedSearchRow
                      key={search.id}
                      search={search}
                      onSelect={() => onSubmitQuery(search.query)}
                    />
                  ))}
                </div>

                <div className="py-(--slate-dimensions-1)">
                  <SectionLabel>Recent searches</SectionLabel>
                  {recentQueries.map((recentQuery) => (
                    <RecentQueryRow
                      key={recentQuery}
                      text={recentQuery}
                      onSelect={() => onSubmitQuery(recentQuery)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface SuggestionRowProps {
  text: string;
  query: string;
  onSelect: () => void;
}

const SuggestionRow: React.FC<SuggestionRowProps> = ({
  text,
  query,
  onSelect,
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === "Enter") {
        onSelect();
      }
    }}
    className="hover:bg-fs-surface-secondary flex cursor-pointer items-center gap-(--slate-dimensions-3) px-(--slate-dimensions-5) py-(--slate-dimensions-1)"
  >
    <div className="bg-fs-surface-secondary flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
      <Search width="16" height="16" />
    </div>
    <span className="type-fs-paragraph-medium text-fs-text-primary">
      <InverseBoldMatch text={text} query={query} />
    </span>
  </div>
);

interface SectionLabelProps {
  children: React.ReactNode;
}

const SectionLabel: React.FC<SectionLabelProps> = ({ children }) => (
  <div className="type-fs-label text-fs-text-subdued px-(--slate-dimensions-5) py-(--slate-dimensions-1)">
    {children}
  </div>
);

interface SuggestedSearchRowProps {
  search: RelatedSearch;
  onSelect: () => void;
}

const SuggestedSearchRow: React.FC<SuggestedSearchRowProps> = ({
  search,
  onSelect,
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === "Enter") {
        onSelect();
      }
    }}
    className="hover:bg-fs-surface-secondary flex cursor-pointer items-center gap-(--slate-dimensions-3) px-(--slate-dimensions-5) py-(--slate-dimensions-1)"
  >
    <img
      src={search.imageUrl}
      alt=""
      className="h-8 w-8 shrink-0 rounded-[4px] object-cover"
      referrerPolicy="no-referrer"
    />
    <span className="type-fs-paragraph text-fs-text-primary flex-1 truncate">
      {search.query}
    </span>
  </div>
);

interface RecentQueryRowProps {
  text: string;
  onSelect: () => void;
}

const RecentQueryRow: React.FC<RecentQueryRowProps> = ({ text, onSelect }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === "Enter") {
        onSelect();
      }
    }}
    className="hover:bg-fs-surface-secondary flex cursor-pointer items-center gap-(--slate-dimensions-3) px-(--slate-dimensions-5) py-(--slate-dimensions-1)"
  >
    <div className="bg-fs-surface-secondary flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
      <RewindClock width="16" height="16" />
    </div>
    <span className="type-fs-paragraph text-fs-text-primary flex-1 truncate">
      {text}
    </span>
  </div>
);

interface BrandRowProps {
  brand: MockBrandRow;
}

const BrandRow: React.FC<BrandRowProps> = ({ brand }) => (
  <div className="flex cursor-pointer items-center gap-(--slate-dimensions-3) py-(--slate-dimensions-1)">
    <div
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
        brand.avatarBackgroundClass
      )}
    >
      <span className="type-fs-label-medium text-fs-text-primary">
        {brand.initial}
      </span>
    </div>
    <span className="type-fs-paragraph text-fs-text-primary">{brand.name}</span>
  </div>
);
